// Shared error-key -> display-text lookup for the flat field-path -> message map
// Form/validate.js returns. Strings are copied verbatim, character for character (including
// curly apostrophes and trailing-period presence/absence), from the console's own
// gs-ui-templates/src/i18n/locales/en.json — there is no i18n library in this repo, so this map
// is the plain lookup every section renders through instead. Three keys deliberately deviate from
// the console's own text because that text is itself a defect there — each is called out with its
// own comment below: no_space_or_special_characters, invalid_package_name_format (unchanged,
// listed only because it's adjacent) and invalid_signature_hash_format. Keys produced only by the
// buttons/carousel/auth-extras checks (validateButtons/validateCarousel/validateAuth in
// validate.js) aren't all console-sourced either — see the "no console equivalent" group below —
// any key missing from here entirely still renders reasonably via the humanize fallback in
// friendlyError.
//
// A value may carry a {{num}} placeholder (currently only max_character_allowed). Callers supply
// it by appending ':<param>' to the key itself, e.g. 'max_character_allowed:25' — see
// friendlyError below.
const MESSAGES = {
  template_name_required: 'Template name is required',
  invalid_template_name: 'Invalid element name, Template names can only contain small letters, numbers and underscores.',
  // Console default helper text (TemplateName/index.tsx:76), not an error — shown under the
  // field whenever there's no error to show. See TemplateName.js.
  template_names_can_only_contain: 'Template names can only contain small letters, numbers and underscores.',
  language_is_required: 'Language is required',
  category_is_required: 'Category is required',
  subcategory_is_required: 'Subcategory is required',
  template_body_is_required: 'Body is required',
  sample_variable_is_required: 'Sample variable is required',
  template_header_is_required: 'Header is required',
  // Curly apostrophe (U+2019) in "can’t", and NO trailing period — both verbatim from the console.
  message_header_cannot_have_new_lines: 'The message header can’t have any new lines, formatted characters, emojis or asterisks',
  invalid_format_only_one_variable_allowed: 'Invalid Format, Only one variable is allowed',
  header_media_type_is_required: 'Media type is required',
  template_sample_media_is_required: 'Sample media is required',
  dynamic_variables_are_not_allowed: 'Dynamic variables are not allowed',
  // Straight apostrophe in "can't", WITH a trailing period — unlike the header message above.
  // That asymmetry is real in the console; preserved here rather than "fixed" to match it.
  message_footer_cannot_have_emoji: "The message footer can't have any newlines or emojis.",
  max_character_allowed: 'Maximum {{num}} characters allowed',
  button_title_is_required: 'Button title is required',
  url_is_required: 'Url is required',
  invalid_url: 'Invalid URL',
  url_type_is_required: 'Url type is required',
  suffix_is_required: 'Suffix is required',
  phone_is_required: 'Phone number is required',
  only_numbers_allowed: 'Only numbers allowed',
  copy_code_is_required: 'Copy code is required',
  only_numbers_with_months_days_allowed: 'Only numbers with months days (1-30) are allowed',
  screen_name_is_required: 'Screen name is required',
  flowid_is_required: 'Flow ID is required',
  description_is_required: 'Description is required',
  card_media_is_required: 'Card media is required',
  offer_title_is_required: 'Offer title is required',
  expiration_time_is_required: 'Expiration time is required',
  only_numbers_are_allowed_between: 'Only numbers are allowed, between 1 to 90',
  this_box_must_be_checked: 'This box must be ticked to submit this template.',
  package_name_is_required: 'Package name is required',
  signature_hash_is_required: 'Signature hash is required',
  autofill_text_is_required: 'Autofill text is required',
  copy_code_text_is_required: 'Copy code text is required',

  // ---- Deliberate deviations from the console — the console's own text is defective here. ----
  // Console's config.ts:584 references key `no_space_or_special_characters`, but that key is
  // absent from its en.json, so the console literally renders the raw snake_case key text to the
  // user. We supply the sentence the key obviously intends.
  no_space_or_special_characters: 'No space or special characters allowed',
  // Already correct in the console's en.json — listed here only because it sits next to
  // invalid_signature_hash_format below.
  invalid_package_name_format: 'Invalid package name format',
  // Console's en.json value for this key is 'Invalid  signature hash format' — a DOUBLE space
  // between "Invalid" and "signature". We use a single space.
  invalid_signature_hash_format: 'Invalid signature hash format',

  // ---- No console equivalent — ours. The console handles these cases structurally (disabled
  // controls) or not at all. ----
  status_is_required: 'Status is required.',
  validity_period_is_required: 'Validity period is required.',
  too_many_buttons: 'Too many buttons.',
  max_buttons_of_type_exceeded: 'Too many buttons of this type.',
  opt_out_button_exceeds_button_limit: 'Adding the opt-out button would exceed the maximum of {{num}} buttons.',
  exactly_one_otp_button_required: 'Exactly one OTP button is required.',
  existing_media_must_be_reuploaded_to_save_changes: "This template's media isn't editable directly — pick a new file to save any changes.",
  // Console-verbatim despite living in this group — no trailing period.
  duplicate_package_signature_pair: 'Package name and signature is not unique',
  // Not console copy — the console makes zero buttons on a carousel card structurally impossible
  // (its global "No of buttons" select only offers 1-2); this UI adds/removes buttons per card
  // individually, so CarouselCardDto's own @ArrayMinSize(1) needs an explicit message here.
  card_button_required: 'Add at least one button to this card.',
  // Not console copy either, same reasoning one level up — see validateCarousel's own comment for
  // why this can't just rely on Carousel.js's mount effect always having run first.
  carousel_requires_at_least_one_card: 'Add at least one card to this carousel.',
  // Not console copy — the console's phone CTA field includes the country code, so it only ever
  // needs presence (phone_is_required above). Our DTO enforces PhoneNumberButtonDto.phone_number's
  // own @MinLength(5) regardless of country code.
  phone_number_min_length: 'Phone number must be at least {{num}} digits.',
  // Not console copy — the console has no client-side check for either LTO backstop below
  // (validateCreateTemplateForm.ts's isValidLto only checks the offer title); Enterprise's own
  // server strings ("Please pass at least 1 website button" / "Please pass buttons in required
  // sequence", config.tpl) name the wire shape, not the fix, so plain wording instead.
  lto_requires_url_button: 'Add at least one URL button to this offer.',
  lto_requires_copy_code_first: 'The copy-code button must be first when expiry is on.',
  // Not console copy either — mirrors ce-api's ValidationUtils.validateRequestContactInfoTemplate
  // in plain wording (no single console string covers header+footer+other-buttons at once).
  request_contact_info_must_be_alone: 'Request Contact Info must be the only button on this template, with no header or footer.',
  // Not console copy — the console has this same gap (createTemplateHooks.ts:529 appends `{{1}}`
  // with no check). See validate.js's checkDynamicUrlBase.
  dynamic_url_missing_path: 'Add a path to the URL, e.g. https://www.example.com/. The variable {{1}} is added at its end.',
  dynamic_url_contains_braces: "Don't type {{1}} in the URL. It's added for you.",
  // Not console copy — the console's own deep-link field reuses its generic http(s)-only URL
  // checker, which would reject a real Android deep link (custom app schemes like
  // myapp://product/123). See validate.js's checkDeepLinkFormat for the deliberate deviation.
  invalid_deep_link: 'Enter a deep link with a scheme, e.g. myapp://product/123',
};

// Never throws, never returns undefined — every call site can render the result directly as
// TextField helperText. Accepts an optional ':<param>' suffix on the key, split on the FIRST
// colon only — e.g. friendlyError('max_character_allowed:25') -> 'Maximum 25 characters allowed'.
// A key with no colon behaves exactly as before.
export function friendlyError(key) {
  if (!key) return '';
  const colon = key.indexOf(':');
  const [baseKey, param] = colon === -1 ? [key, undefined] : [key.slice(0, colon), key.slice(colon + 1)];
  const message = MESSAGES[baseKey];
  if (!message) return baseKey.charAt(0).toUpperCase() + baseKey.slice(1).replace(/_/g, ' ');
  return param === undefined ? message : message.replace('{{num}}', param);
}
